import { useState, useEffect, useRef } from 'react'

const usePrizeSlideshow = (prizeArray = [], duration = 5000) => {
	const [currentIndex, setCurrentIndex] = useState(0)
	const [isVisible, setIsVisible] = useState(true)
	const intervalRef = useRef(null)
	const timeoutRef = useRef(null)

	// Reset to first prize when array changes
	useEffect(() => {
		if (prizeArray.length > 0) {
			setCurrentIndex(0)
			setIsVisible(true)
		}
	}, [prizeArray])

	// Auto-advance slideshow
	useEffect(() => {
		if (prizeArray.length <= 1) {
			// Don't cycle if only one or no prizes
			return
		}

		const advancePrize = () => {
			// Fade out current prize
			setIsVisible(false)

			// After fade out completes, change index and fade in
			timeoutRef.current = setTimeout(() => {
				setCurrentIndex((prevIndex) => 
					(prevIndex + 1) % prizeArray.length
				)
				setIsVisible(true)
			}, 300) // Match fade transition duration
		}

		// Start cycling
		intervalRef.current = setInterval(advancePrize, duration)

		// Cleanup
		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current)
			}
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current)
			}
		}
	}, [prizeArray, duration])

	// Manual navigation methods
	const goToNext = () => {
		if (prizeArray.length <= 1) return
		
		setIsVisible(false)
		timeoutRef.current = setTimeout(() => {
			setCurrentIndex((prevIndex) => 
				(prevIndex + 1) % prizeArray.length
			)
			setIsVisible(true)
		}, 300)

		// Reset interval
		if (intervalRef.current) {
			clearInterval(intervalRef.current)
			intervalRef.current = setInterval(() => {
				// Repeat the advance logic
				setIsVisible(false)
				setTimeout(() => {
					setCurrentIndex((prevIndex) => 
						(prevIndex + 1) % prizeArray.length
					)
					setIsVisible(true)
				}, 300)
			}, duration)
		}
	}

	const goToPrev = () => {
		if (prizeArray.length <= 1) return
		
		setIsVisible(false)
		timeoutRef.current = setTimeout(() => {
			setCurrentIndex((prevIndex) => 
				prevIndex === 0 ? prizeArray.length - 1 : prevIndex - 1
			)
			setIsVisible(true)
		}, 300)

		// Reset interval
		if (intervalRef.current) {
			clearInterval(intervalRef.current)
			intervalRef.current = setInterval(() => {
				// Repeat the advance logic
				setIsVisible(false)
				setTimeout(() => {
					setCurrentIndex((prevIndex) => 
						(prevIndex + 1) % prizeArray.length
					)
					setIsVisible(true)
				}, 300)
			}, duration)
		}
	}

	const goToIndex = (index) => {
		if (index < 0 || index >= prizeArray.length) return
		
		setIsVisible(false)
		timeoutRef.current = setTimeout(() => {
			setCurrentIndex(index)
			setIsVisible(true)
		}, 300)

		// Reset interval
		if (intervalRef.current) {
			clearInterval(intervalRef.current)
			intervalRef.current = setInterval(() => {
				// Repeat the advance logic
				setIsVisible(false)
				setTimeout(() => {
					setCurrentIndex((prevIndex) => 
						(prevIndex + 1) % prizeArray.length
					)
					setIsVisible(true)
				}, 300)
			}, duration)
		}
	}

	// Pause/Resume functionality
	const pause = () => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current)
			intervalRef.current = null
		}
	}

	const resume = () => {
		if (!intervalRef.current && prizeArray.length > 1) {
			intervalRef.current = setInterval(() => {
				setIsVisible(false)
				setTimeout(() => {
					setCurrentIndex((prevIndex) => 
						(prevIndex + 1) % prizeArray.length
					)
					setIsVisible(true)
				}, 300)
			}, duration)
		}
	}

	return {
		currentIndex,
		isVisible,
		currentPrize: prizeArray[currentIndex] || null,
		totalPrizes: prizeArray.length,
		goToNext,
		goToPrev, 
		goToIndex,
		pause,
		resume,
		hasMultiple: prizeArray.length > 1
	}
}

export default usePrizeSlideshow
